import React from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import tw from 'tailwind.macro';
import { NeverProps } from '../../../common/types';
import { useWindowInnerWidth } from '../../../hooks';
import { ChevronIcon } from '../../../images/icons/chevron';
import { CloseIcon } from '../../../images/icons/close';
import Nodi from '../../../images/illustrations/nodi.webp';
import { colors, screensInt } from '../../../styles/constants';
import Button from '../../Forms/Button';
import { MainContentWrapper } from '../Header';
import FunnelNavigation from '../Navigation';
import {
  PanelCloseButton,
  PanelContainer,
  PanelContent,
  PanelHeader,
  PanelImage,
  PanelTitle,
  PanelWrapper,
} from './common';
import {
  PANEL_CLOSE_BUTTON_TEST_ID,
  PANEL_CTA_BUTTON_TEST_ID,
} from './constants';
import { PanelProps } from './types';

type MainPanelProps = {
  withoutNodiImage?: boolean;
  withFunnelMobileHeader?: boolean;
  withoutHeader?: boolean;
  headerImage?: {
    src: string;
    alt: string;
    srcSet?: string;
  };
} & PanelProps &
  (MainPanelCTAProps | NeverProps<MainPanelCTAProps>);

type MainPanelCTAProps = {
  CTAButtonContent: React.ReactNode;
  CTAButtonFormId?: string;
  CTAButtonId?: string;
  onCTAButtonClick: () => void;
  CTALeftContent?: React.ReactNode;
  CTAContainerStyle?: FlattenSimpleInterpolation;
  isCTAButtonDisabled?: boolean;
  isCTAButtonLoading?: boolean;
  desktopBreakpoint?: number;
  CTALoadingMessage?: React.ReactNode;
};

export const MainPanel: React.FC<MainPanelProps> = ({
  children,
  closeButtonRef,
  CTAButtonContent,
  CTAContainerStyle,
  CTALeftContent,
  headerImage,
  isCTAButtonDisabled,
  isCTAButtonLoading,
  onClose,
  onCTAButtonClick,
  testid,
  title,
  withFunnelMobileHeader,
  withoutHeader,
  withoutNodiImage,
  desktopBreakpoint = screensInt.md,
  CTAButtonFormId,
  CTAButtonId,
  CTALoadingMessage,
}) => {
  const innerWidth = useWindowInnerWidth();

  return (
    <PanelContainer
      data-testid={testid}
      desktopBreakpoint={desktopBreakpoint}
      withCTA={!!CTAButtonContent}
      withFunnelMobileHeader={withFunnelMobileHeader}
    >
      {!withoutHeader && (
        <React.Fragment>
          {innerWidth >= desktopBreakpoint || !withFunnelMobileHeader ? (
            <PanelHeader>
              {!withoutNodiImage && (
                <div>
                  {headerImage ? (
                    <PanelImage
                      alt={headerImage.alt}
                      src={headerImage.src}
                      srcSet={headerImage.srcSet}
                    />
                  ) : (
                    <PanelImage alt="Nodi" src={Nodi} />
                  )}
                </div>
              )}
              <PanelTitle>{title}</PanelTitle>

              <PanelCloseButton
                ref={closeButtonRef}
                data-testid={PANEL_CLOSE_BUTTON_TEST_ID}
                onClick={(event) => onClose(event)}
                type="button"
              >
                <CloseIcon color={colors.text} />
              </PanelCloseButton>
            </PanelHeader>
          ) : (
            <FunnelNavigation
              Container={MainContentWrapper}
              leftButtonIcon={<ChevronIcon />}
              onLeftButtonClick={(event) => onClose(event)}
            >
              <PanelTitle style={tw`text-white`}>{title}</PanelTitle>
            </FunnelNavigation>
          )}
        </React.Fragment>
      )}

      <PanelContent>{children}</PanelContent>
      {CTAButtonContent && (
        <PanelCTA
          containerStyle={CTAContainerStyle}
          disabled={isCTAButtonDisabled}
          form={CTAButtonFormId}
          id={CTAButtonId}
          isLoading={isCTAButtonLoading}
          leftContent={CTALeftContent}
          loadingMessage={CTALoadingMessage}
          onClick={onCTAButtonClick}
        >
          {CTAButtonContent}
        </PanelCTA>
      )}
    </PanelContainer>
  );
};

const ConfirmContainer = styled(PanelWrapper)<{
  customStyle: FlattenSimpleInterpolation;
}>`
  border-top: 1px solid;
  ${tw`sticky w-full py-medium bg-white border-grey-300 flex items-center 
  `}
  padding-bottom:max(var(--size-medium) ,env(safe-area-inset-bottom));
  bottom: 0;
  gap: var(--size-big);

  ${({ customStyle }) => customStyle}
`;

const LeftContentContainer = styled.div`
  ${tw`whitespace-no-wrap`}
`;

const ConfirmButtonStyled = css`
  ${tw`w-full`}
`;

interface PanelCTAProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  leftContent?: React.ReactNode;
  containerStyle?: FlattenSimpleInterpolation;
  isLoading?: boolean;
  loadingMessage?: React.ReactNode;
}

export const PanelCTA: React.FC<PanelCTAProps> = ({
  children,
  containerStyle,
  disabled,
  form,
  isLoading,
  leftContent,
  loadingMessage,
  onClick,
  ...props
}) => (
  <ConfirmContainer customStyle={containerStyle}>
    {leftContent && <LeftContentContainer>{leftContent}</LeftContentContainer>}

    <Button
      css={ConfirmButtonStyled}
      data-testid={PANEL_CTA_BUTTON_TEST_ID}
      disabled={disabled}
      form={form}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      onClick={onClick}
      type={form ? 'submit' : 'button'}
      variant="PRIMARY"
      {...props}
    >
      {children}
    </Button>
  </ConfirmContainer>
);
